<template>
  <div class="w-100 my-4" v-if="warehouses?.length">
    <v-data-table
      :headers="headers"
      :items="internalItems"
      hide-default-footer
      class="elevation-0"
    >
      <template v-slot:top>
        <div class="d-flex justify-space-between align-center py-3">
          <span class="text-dark-50 mt-1 font-size-h5 font-weight-bolder">
            WMS Warehouses
          </span>
          <button
            type="button"
            class="btn btn-light-info"
            :disabled="
              honeywellAdded || internalItems.length === wmses.length - 1
            "
            @click="showAddItemModal"
          >
            New item
          </button>
        </div>
      </template>

      <!-- WMS name column -->
      <template v-slot:item.wms_id="{ item }">
        {{ getWmsName(item.wms_id) }}
      </template>

      <!-- Warehouses column -->
      <template v-slot:item.warehouse_id="{ item }">
        <div
          class="d-flex flex-wrap gap-1 align-items-center justify-content-center"
        >
          <span
            v-for="whId in item.warehouse_id"
            :key="whId"
            class="badge badge-light-info mr-1 mb-1 py-1 px-2"
          >
            {{ getWarehouseName(whId) }}
          </span>
        </div>
      </template>

      <!-- Actions column -->
      <template v-slot:item.actions="{ item }">
        <div class="row w-100 justify-center">
          <button class="mx-2" @click="() => showEditItemModal(item)">
            <v-icon> mdi-pencil </v-icon>
          </button>
          <button @click="() => removeItem(item.wms_id)">
            <v-icon class=""> mdi-delete </v-icon>
          </button>
        </div>
      </template>
    </v-data-table>

    <AddWMSWarehouse
      ref="editItem"
      :actionFunction="handleAddOrEdit"
      :wmses="filteredWmses"
      :warehouses="warehouses"
      :wms_warehouses="wms_warehouses"
      :existingItems="internalItems"
    />
  </div>
</template>

<script>
import AddWMSWarehouse from "@/own/components/customers/clients/FormParts/WMSWarehouseDatatable/AddWMSWarehouse.vue";
import Swal from "sweetalert2";

export default {
  name: "WMSWarehouseDatatable",
  components: { AddWMSWarehouse },
  props: {
    actionItem: {
      default: () => false,
    },
    wmses: {
      type: Array,
      default: () => [],
    },
    warehouses: {
      type: Array,
      default: () => [],
    },
    wms_warehouses: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      internalItems: [],
      /** wms_id of the row currently open in the edit popup; null when adding */
      editingWmsId: null,
    };
  },
  mounted() {
    this.resetToDefaultValues();
  },
  computed: {
    /**
     * Find the index (id) of the Honeywell WMS from the wmses prop.
     * Matched by text so it's not hardcoded to a specific index value.
     */
    honeywellWmsId() {
      const found = this.wmses.find(
        (w) => w.text && w.text.toLowerCase() === "honeywell"
      );
      return found ? found.index : null;
    },

    /**
     * True when a Honeywell row already exists in the datatable.
     * Disables the "New item" button because no more rows can be added
     * once Honeywell is present.
     */
    honeywellAdded() {
      if (this.honeywellWmsId === null) return false;
      return this.internalItems.some(
        (item) => item.wms_id === this.honeywellWmsId
      );
    },

    /**
     * WMS options passed to the popup.
     * Honeywell is hidden when at least one OTHER non-Honeywell row exists
     * in the datatable (excluding the row currently being edited).
     *
     * This enforces mutual exclusivity:
     *   - Honeywell cannot coexist with IQH/other WMS entries.
     *   - If editing the only non-Honeywell row, Honeywell reappears so
     *     the user can switch that row to Honeywell.
     */
    filteredWmses() {
      if (this.honeywellWmsId === null) return this.wmses;

      // When editing the Honeywell row itself — always show all options
      if (this.editingWmsId === this.honeywellWmsId) return this.wmses;

      // Count non-Honeywell rows, ignoring the row being edited
      const otherNonHoneywellCount = this.internalItems.filter(
        (item) =>
          item.wms_id !== this.honeywellWmsId &&
          item.wms_id !== this.editingWmsId
      ).length;

      if (otherNonHoneywellCount > 0) {
        // Hide Honeywell — other WMS entries already exist
        return this.wmses.filter((w) => w.index !== this.honeywellWmsId);
      }
      return this.wmses;
    },

    headers() {
      return [
        {
          text: "WMS",
          value: "wms_id",
          align: "center",
          class: "text-muted fw-bolder text-uppercase poppins-ls",
          cellClass: "text-dark-75 font-weight-medium poppins-ls",
        },
        {
          text: "Warehouses",
          value: "warehouse_id",
          align: "center",
          sortable: false,
          class: "text-muted fw-bolder text-uppercase poppins-ls",
          cellClass: "text-dark-75 font-weight-medium poppins-ls max-w-350px",
        },
        {
          text: "Actions",
          value: "actions",
          align: "center",
          sortable: false,
          class: "text-muted fw-bolder text-uppercase poppins-ls",
          cellClass: "text-dark-75 font-weight-medium poppins-ls",
        },
      ];
    },
  },
  methods: {
    resetToDefaultValues() {
      if (this.wms_warehouses?.length && !this.actionItem) {
        this.internalItems = [
          {
            wms_id: 2,
            warehouse_id: this.wms_warehouses.find((w) => w.wms_id === 2)
              ?.warehouses,
          },
        ];
      }
    },
    getWmsName(wmsId) {
      const found = this.wmses.find((w) => w.index === wmsId);
      return found ? found.text : wmsId;
    },

    getWarehouseName(whId) {
      const found = this.warehouses.find((w) => w.index === whId);
      return found ? found.text : whId;
    },

    showAddItemModal() {
      this.editingWmsId = null;
      this.$refs.editItem.toggleModal(null);
    },

    showEditItemModal(item) {
      this.editingWmsId = item.wms_id;
      this.$refs.editItem.toggleModal(item);
    },

    /**
     * Called by AddWMSWarehouse on submit.
     * @param {Object} formData - { wms_id, warehouse_id }
     * @param {boolean} isEditing - true when editing an existing row
     * @param {number|null} originalWmsId - the wms_id of the row being edited
     */
    handleAddOrEdit(formData, isEditing, originalWmsId) {
      if (isEditing) {
        // Find the row being edited by its original wms_id and replace it
        const index = this.internalItems.findIndex(
          (item) => item.wms_id === originalWmsId
        );
        if (index !== -1) {
          this.$set(this.internalItems, index, { ...formData });
        }
      } else {
        // Check if wms_id already has a row
        const exists = this.internalItems.some(
          (item) => item.wms_id === formData.wms_id
        );
        if (exists) {
          Swal.fire({
            title: "Warning",
            text: "This WMS has already been added! Edit the existing row instead.",
            icon: "warning",
            showConfirmButton: true,
          });
          return;
        }
        this.internalItems.push({ ...formData });
      }
    },

    removeItem(wmsId) {
      const index = this.internalItems.findIndex(
        (item) => item.wms_id === wmsId
      );
      if (index === -1) {
        Swal.fire({
          title: "Error",
          text: "Can not find the item to delete!",
          icon: "error",
          showConfirmButton: false,
          timer: 2500,
        });
      } else {
        Swal.fire({
          title: "Warning?",
          text: "Do you want delete this item ?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        }).then((result) => {
          if (result.isConfirmed) {
            this.internalItems.splice(index, 1);
          }
        });
      }
    },

    /** Called by BasicFormFields via $refs.wmsWarehouse.getItems() when building form data */
    getItems() {
      return this.internalItems;
    },

    /** Called by BasicFormFields via $refs.wmsWarehouse.setItems() when populating edit data */
    setItems(items) {
      this.internalItems = items ? [...items] : [];
    },
  },
};
</script>
